import React from 'react';
import { Check } from 'lucide-react';

interface Message {
  id: string;
  senderType: 'owner' | 'guest';
  text: string;
  translatedText?: string;
  timestamp?: string;
  culturalNotes?: string;
  meaningExplanation?: string;
}

interface FlashcardProps {
  message: Message;
  userType: 'owner' | 'guest';
  isFlipped: boolean;
  onFlip: () => void;
  justSent?: boolean;
}

export const Flashcard: React.FC<FlashcardProps> = ({ 
  message, 
  userType, 
  isFlipped, 
  onFlip, 
  justSent = false,
}) => {
  console.log('🃏 Flashcard render:', { 
    messageId: message.id, 
    text: message.text?.substring(0, 30), 
    translatedText: message.translatedText?.substring(0, 30),
    senderType: message.senderType,
    userType 
  });

  const isOwnMessage = message.senderType === userType;
  const isRecipientView = !isOwnMessage;
  
  // Determine what text to show on front and back (with safe fallbacks)
  const safe = (s?: string) => (typeof s === 'string' ? s : '');
  const has = (s?: string) => !!(s && s.trim() !== '');
  // For recipients, hide original until translation is ready; show placeholder instead
  const primary = isOwnMessage
    ? safe(message.text)
    : has(message.translatedText)
      ? safe(message.translatedText)
      : '';
  // Secondary: own => translated then original; recipient => original only after translation exists
  const secondary = isOwnMessage
    ? (has(message.translatedText) ? safe(message.translatedText) : safe(message.text))
    : (has(message.translatedText) ? safe(message.text) : '');
  
  // Avoid showing '(no text)' for recipients while translation is pending (ignore _pending state)
  const isTranslatingForRecipient = isRecipientView && !has(message.translatedText)
  const frontText = isTranslatingForRecipient ? '' : (primary || '')
  const backText = isTranslatingForRecipient ? '' : (secondary || '')
  
  // Only owners can flip the card to see details, and only for guest messages
  const canFlip = userType === 'owner' && message.senderType === 'guest';
  
  const cardScheme = {
    owner: {
      front: 'owner-message',
      back: 'owner-message-back',
      badge: 'owner-badge',
    },
    guest: {
      front: 'guest-message', 
      back: 'guest-message-back',
      badge: 'guest-badge',
    }
  };

  const scheme = cardScheme[message.senderType];

  // Safely compute display time
  const rawTime: any = (message as any).timestamp ?? (message as any).createdAt ?? (message as any).created_at;
  let timeText = '';
  try {
    if (rawTime) {
      const d = new Date(rawTime);
      if (!isNaN(d.getTime())) {
        timeText = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
    }
  } catch {}

  const handleCardClick = () => {
    if (canFlip) {
      onFlip();
    }
  };

  return (
    <div className="flashcard-container relative perspective-1000">
      <div 
        className={`flashcard ${isFlipped && canFlip ? 'is-flipped' : ''} ${canFlip ? 'cursor-pointer' : 'cursor-default'}`}
        onClick={handleCardClick}
      >
        {/* Front Face */}
        <div className={`flashcard-face flashcard-front ${scheme.front}`}>
          <div className="p-6 md:p-8 h-full flex flex-col justify-between relative">
            <div className="flex justify-between items-start">
              <span className={scheme.badge}>
                {message.senderType === 'owner' ? 'Owner' : 'Guest'}
              </span>
              {timeText && (
                <span className="text-white/60 text-sm">
                  {timeText}
                </span>
              )}
            </div>
            
            <div className="flex-grow flex items-center justify-center px-4">
              <div className="text-center">
                <p className="text-xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
                  {frontText}
                </p>
                {isTranslatingForRecipient && (
                  <div className="mt-3 inline-flex items-center gap-2 text-white/80 text-sm">
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
                    <span>Translating…</span>
                  </div>
                )}
              </div>
            </div>
            
            {canFlip && (
              <div className="text-center text-white/70 text-sm">
                Tap to see original & cultural notes
              </div>
            )}
            
            {(message as any)._pending && (
              <div className="absolute bottom-3 right-3 bg-black/30 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
                <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
                <span>Sending…</span>
              </div>
            )}
            
            {!((message as any)._pending) && justSent && (
              <div className="absolute bottom-3 right-3 bg-white/90 text-green-700 text-xs px-2 py-1 rounded-md shadow-sm flex items-center gap-1 animate-fade-out">
                <Check className="w-3.5 h-3.5" />
                <span>Sent</span>
              </div>
            )}
          </div>
        </div>

        {/* Back Face - Only for owners viewing guest messages */}
        {canFlip && (
          <div className={`flashcard-face flashcard-back ${scheme.back}`}>
            <div className="p-4 md:p-6 h-full flex flex-col">
              <div className="flex justify-between items-start mb-3">
                <span className={scheme.badge}>
                  Original Message
                </span>
                {timeText && (
                  <span className="text-white/60 text-sm">
                    {timeText}
                  </span>
                )}
              </div>
              
              {/* Original Text Section - Fixed Height */}
              <div className="flex-shrink-0 text-center mb-4 pb-4 border-b border-white/20">
                <p className="text-lg md:text-xl font-semibold text-white leading-tight">
                  {backText}
                </p>
              </div>
              
              {/* Scrollable Content Section */}
              <div className="flex-1 overflow-y-auto scrollbar-thin">
                {(message.meaningExplanation || message.culturalNotes) ? (
                  <div className="space-y-3">
                    {message.meaningExplanation && (
                      <div className="p-3 bg-black/20 rounded-lg text-white/90 text-sm">
                        <h4 className="font-bold mb-2 flex items-center gap-2 text-white text-xs">
                          <span>🧠</span> What this means:
                        </h4>
                        <p className="leading-relaxed text-white/90 text-xs">{message.meaningExplanation}</p>
                      </div>
                    )}
                    {message.culturalNotes && (
                      <div className="p-3 bg-black/20 rounded-lg text-white/90 text-sm">
                        <h4 className="font-bold mb-2 flex items-center gap-2 text-white text-xs">
                          <span>🌍</span> Cultural context:
                        </h4>
                        <p className="leading-relaxed text-white/90 text-xs">{message.culturalNotes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-white/60 text-sm italic">
                    No additional context available
                  </div>
                )}
              </div>
              
              {/* Fixed Footer */}
              <div className="flex-shrink-0 text-center text-white/70 text-xs mt-3 pt-3 border-t border-white/20">
                Tap to flip back
              </div>
            </div>
          </div>
        )}
      </div>

      {/* In-card navigation removed for vertical list UX */}
    </div>
  );
};
